/**
 * The single fullscreen pass, and the much smaller thing it becomes in a
 * headset.
 *
 * The scene is drawn into a render target and then blitted to the canvas
 * through one custom shader that does grain, vignette, chromatic aberration,
 * barrel warp, desaturation, a sickly tint, a damage flash and — only when the
 * player is nearly gone — scanlines and dropouts. No `three/examples/jsm`.
 *
 * Restraint is the whole design. At sanity 0 the shader is a whisper of grain
 * and a hint of vignette; everything else is keyed off `s` or `s * s` so it
 * arrives late and the player notices the room getting wrong before they
 * notice the screen getting wrong.
 *
 * Colour pipeline: three disables tone mapping when it renders into a render
 * target, so this pass has to finish the job. When the renderer has no tone
 * mapping the target stores sRGB (8 bits of linear in a game this dark bands
 * badly); when it does, the target is half-float linear and the tone curve is
 * applied here.
 *
 * ## Inside an immersive session
 *
 * None of the above can run. Three reasons, in order of how expensive they are
 * to learn the hard way:
 *
 * 1. **The framebuffer is not ours.** `WebXRManager` binds the session's
 *    render target at the top of every XR frame. The `setRenderTarget(null)`
 *    at the end of `render` would unbind it and draw the game to the flat
 *    canvas nobody is looking at — a black headset, in both the enabled and
 *    the bypassed path.
 * 2. **There is no "the screen".** three has no multiview here: an
 *    `ArrayCamera` is drawn by looping the render list once per eye with that
 *    eye's viewport. One fullscreen triangle across the whole target would
 *    centre its warp and vignette on the seam between two eyes, not on either
 *    pupil.
 * 3. **It is the wrong thing to spend the frame on anyway.** An offscreen pass
 *    means writing the whole stereo frame to a texture and reading it back:
 *    two extra passes over ~6 Mpx at 72 Hz, and — worse on a tiler — it throws
 *    away the two things that make headset rendering affordable, the foveated
 *    resolve and never leaving tile memory.
 *
 * So in a session the scene renders straight through, and the grade becomes a
 * **blend-only overlay**: the same fullscreen triangle, drawn per eye by the
 * same ArrayCamera loop, with no texture read. What that buys and what it
 * costs is written out at `XR_FRAGMENT` — the short version is that grain, the
 * vignette, the damage flash and the dropouts survive intact, the sanity
 * desaturation survives as an approximation, and barrel warp, chromatic
 * aberration and scanlines are gone. The first two were going to make people
 * ill; the third is a moving head-locked stripe pattern, which is the same
 * problem.
 *
 * Colour is not this pass's job in a session: three treats the XR target as
 * `isXRRenderTarget` and applies tone mapping and the output transfer function
 * in each material's own shader, exactly as it does when drawing to a canvas.
 */

import * as THREE from 'three';
import { TUNING } from '../core/config';
import { clamp01, damp, smoothstep } from '../core/math';
import type { LevelSpec } from '../core/types';
import type { PostFx } from './api';
import { xrComfort, xrPostGrade, xrQuality } from './xrquality';

const VERTEX = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const FRAGMENT = /* glsl */ `
uniform sampler2D tDiffuse;
uniform vec2 uResolution;
uniform float uPixelRatio;
uniform float uTime;
uniform float uSanity;
uniform float uDamage;
uniform float uDropout;
uniform float uDrain;
uniform float uExposure;
uniform int uTone;

varying vec2 vUv;

const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);

// Hash without sine (Dave Hoskins). Stable across drivers, unlike sin-based hashes.
float hash13(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.zyx + 31.32);
  return fract((p.x + p.y) * p.z);
}

// Narkowicz's fit. Close enough to three's ACES curve for a horror game.
vec3 acesFilmic(vec3 x) {
  return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0);
}

vec3 cineon(vec3 x) {
  x = max(vec3(0.0), x - 0.004);
  return pow((x * (6.2 * x + 0.5)) / (x * (6.2 * x + 1.7) + 0.06), vec3(2.2));
}

vec3 toSrgb(vec3 c) {
  c = clamp(c, 0.0, 1.0);
  return mix(c * 12.92, 1.055 * pow(c, vec3(0.41666)) - 0.055, step(0.0031308, c));
}

void main() {
  float s = clamp(uSanity, 0.0, 1.0);
  float s2 = s * s;

  vec2 d = vUv - 0.5;
  float aspect = uResolution.x / max(uResolution.y, 1.0);
  vec2 rv = vec2(d.x * aspect, d.y);
  float r2 = dot(rv, rv);
  float r = sqrt(r2);
  float maxR2 = 0.25 * (aspect * aspect + 1.0);

  // Barrel warp, normalised so the corners stay pinned to the corners and the
  // pass never samples outside the target.
  float k = 0.012 + 0.20 * s2;
  vec2 warped = 0.5 + d * ((1.0 + k * r2) / (1.0 + k * maxR2));

  // Radial chromatic aberration: nothing at the centre, splitting at the edge.
  float ca = (0.0005 + 0.0080 * s2) * (0.25 + r);
  vec2 dir = d / max(length(d), 1e-4);
  vec3 col;
  col.r = texture2D(tDiffuse, warped + dir * ca).r;
  col.g = texture2D(tDiffuse, warped).g;
  col.b = texture2D(tDiffuse, warped - dir * ca).b;

  if (uTone == 1) col = clamp(col * uExposure, 0.0, 1.0);
  else if (uTone == 2) { col *= uExposure; col = col / (vec3(1.0) + col); }
  else if (uTone == 3) col = cineon(col * uExposure);
  else if (uTone == 4) col = acesFilmic(col * uExposure);

  vec3 c = toSrgb(col);

  // Film grain, quantised to 24Hz so it stutters like film rather than boiling.
  vec2 grainPx = floor(vUv * uResolution / max(uPixelRatio, 1.0));
  float g = hash13(vec3(grainPx, floor(uTime * 24.0)));
  c += (g - 0.5) * (0.028 + 0.085 * s + 0.13 * uDamage);

  // Colour drains out and goes sour well before anything else is obvious, and
  // the whole image cools off — a room you are losing rather than a filter.
  float sick = smoothstep(0.30, 1.0, s);
  float lum = dot(c, LUMA);
  c = mix(c, vec3(lum), 0.6 * sick);
  c *= mix(vec3(1.0), vec3(0.90, 1.02, 0.66), sick);
  vec3 crushed = clamp(c, 0.0, 1.0);
  c = mix(c, crushed * crushed * (3.0 - 2.0 * crushed) * 0.88, sick * 0.7);

  if (uDamage > 0.001) {
    float dl = dot(c, LUMA);
    c = mix(c, vec3(dl * 1.35 + 0.14, dl * 0.16, dl * 0.13), uDamage * 0.68);
  }

  // Scanlines only turn up at the very bottom, and they drift.
  float deep = smoothstep(0.60, 1.0, s);
  if (deep > 0.0) {
    float y = vUv.y * uResolution.y / max(uPixelRatio, 1.0);
    float drift = uTime * 11.0 + sin(uTime * 0.23) * 40.0;
    c *= 1.0 - 0.06 * deep * (0.5 + 0.5 * sin((y + drift) * 0.9));
  }

  // Standing inside a drained room. Deliberately the smallest contribution in
  // the whole pass: the ceiling going out is what sells the effect, and a
  // heavy filter on top of it would just look like a filter. All this does is
  // stop the edges of the frame from arguing with the dark in the middle.
  if (uDrain > 0.001) {
    float dl = dot(c, LUMA);
    c = mix(c, vec3(dl), 0.22 * uDrain);
  }

  float vig = 1.0 - smoothstep(mix(0.58, 0.20, s) - 0.10 * uDrain, mix(1.35, 0.85, s) - 0.20 * uDrain, r);
  c *= mix(1.0, vig, clamp(mix(0.22, 1.0, s) + uDamage * 0.30 + 0.40 * uDrain, 0.0, 1.0));

  // A single frame of nothing. Rare, and only when sanity is almost gone.
  c = mix(c, vec3(g * 0.14), uDropout * 0.9);

  gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
}
`;

/**
 * The session grade: everything above that can be done without reading the
 * frame, and nothing that cannot.
 *
 * The pass has no `tDiffuse`. It is one alpha-blended triangle drawn over the
 * finished scene, so the only operation available is `mix(frame, colour,
 * alpha)` — per pixel, but blind. Written premultiplied so the whole grade
 * composites in one draw: each layer is folded in as `over`, and the result is
 * `frame * (1 - A) + acc`, which is what `premultipliedAlpha` asks the blender
 * for.
 *
 * What that costs, effect by effect:
 *
 * - **Grain** is exact in feel. Adding a signed offset is not available, so it
 *   mixes toward the noise value instead; the maths is the same near mid grey
 *   and slightly brighter in the shadows, which is why `xr.grainScale` trims
 *   it. Kept because it is the cheapest thing in the chain and does a
 *   disproportionate amount of the work: it is the difference between a clean
 *   render and a room being *filmed*.
 * - **The vignette** is exact — the flat pass multiplies by `vig`, and a
 *   multiply toward black is a mix toward black. Its comfort term is switched
 *   off in `TUNING.render.xr` and kept only as a fallback, because
 *   `render/xrvignette.ts` already draws a proper angular comfort tunnel tied
 *   to what the player is doing. Two darkenings would be one too many.
 * - **The damage flash and the dropouts** survive; the dropout is scaled back
 *   because a single black frame is a much bigger event 3 cm from an eye.
 * - **The sanity grade is an approximation and this is the real loss.** The
 *   flat pass desaturates toward luminance and then tints; a blind pass cannot
 *   compute luminance, so it mixes toward a sour olive instead. On the yellow
 *   the game is mostly made of, the two land close — the colour goes out of
 *   the wallpaper and the room turns sick. On anything strongly off-hue, a red
 *   stain, a monster's mouth, it does not: that stays red where on a monitor it
 *   would have gone grey. The horror still arrives, slightly less specifically.
 * - **Barrel warp and chromatic aberration are cut**, and would have been cut
 *   even if they were affordable. Warping the projection makes the world
 *   disagree with the geometry the inner ear is being told about, and the
 *   headset already applies its own lens distortion and its own chromatic
 *   correction — fighting them is eye strain at best. **Scanlines are cut**
 *   for the same family of reasons: head-locked, moving, high contrast.
 *
 * The hash is deliberately duplicated from `FRAGMENT` rather than shared. The
 * flat pass is what the game has been playing on for months and its shader
 * source is left untouched, byte for byte.
 */
const XR_FRAGMENT = /* glsl */ `
uniform vec2 uResolution;
uniform float uTime;
uniform float uSanity;
uniform float uDamage;
uniform float uDropout;
uniform float uDrain;
uniform float uComfort;
uniform float uGrain;
uniform float uGrade;

varying vec2 vUv;

/** Where the colour goes when it goes. Stands in for desaturate-then-tint. */
const vec3 SOUR = vec3(0.30, 0.34, 0.16);
const vec3 BLOOD = vec3(0.42, 0.04, 0.03);

// Hash without sine (Dave Hoskins). Stable across drivers, unlike sin-based hashes.
float hash13(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.zyx + 31.32);
  return fract((p.x + p.y) * p.z);
}

// Fold one layer into the premultiplied accumulator.
#define OVER(c, a) { float w = clamp(a, 0.0, 1.0) * (1.0 - A); acc += (c) * w; A += w; }

void main() {
  float s = clamp(uSanity, 0.0, 1.0);

  // Radius normalised so the corner is 1.0 whatever shape the viewport is. An
  // eye is very nearly square where a monitor is 16:9, and the flat pass's
  // radii are calibrated against a 16:9 corner. Used raw here, the vignette
  // would never engage at all.
  // (This shader's comments are deliberately plain ASCII: it is the one shader
  // that only ever compiles on a device nobody here can test.)
  vec2 d = vUv - 0.5;
  float aspect = uResolution.x / max(uResolution.y, 1.0);
  float r = length(vec2(d.x * aspect, d.y)) / (0.5 * sqrt(aspect * aspect + 1.0));

  vec3 acc = vec3(0.0);
  float A = 0.0;

  // Grain, quantised to 24Hz as on the flat pass. The stutter matters more
  // here than there: noise that boils every frame is exactly the signal a
  // headset's reprojection is trying to track motion with.
  vec2 grainPx = floor(vUv * uResolution);
  float g = hash13(vec3(grainPx, floor(uTime * 24.0)));
  OVER(vec3(g), (0.028 + 0.085 * s + 0.13 * uDamage) * uGrain)

  float sick = smoothstep(0.30, 1.0, s);
  OVER(SOUR, sick * uGrade)

  OVER(BLOOD, uDamage * 0.5)

  // Standing inside a drained room. As on the flat pass, deliberately the
  // smallest contribution: the ceiling going out is what sells it.
  OVER(vec3(0.0), 0.16 * uDrain)

  // The flat pass multiplies the frame by vig; a multiply toward black is a
  // mix toward black, so this layer is the same picture, not an impression.
  float vig = 1.0 - smoothstep(mix(0.55, 0.20, s) - 0.10 * uDrain, mix(1.25, 0.82, s) - 0.20 * uDrain, r);
  float vigA = (1.0 - vig) * clamp(mix(0.22, 1.0, s) + uDamage * 0.30 + 0.40 * uDrain, 0.0, 1.0);
  // The comfort term is a floor, not a sum: it is doing a different job and
  // should not stack into a tunnel when the player is also losing their mind.
  vigA = max(vigA, uComfort * smoothstep(0.40, 0.95, r));
  OVER(vec3(0.0), vigA)

  OVER(vec3(g * 0.14), uDropout)

  gl_FragColor = vec4(acc, A);
}
`;

/** Shader branch index for a three tone mapping constant. */
function toneMode(mapping: THREE.ToneMapping): number {
  switch (mapping) {
    case THREE.NoToneMapping:
      return 0;
    case THREE.LinearToneMapping:
      return 1;
    case THREE.ReinhardToneMapping:
      return 2;
    case THREE.CineonToneMapping:
      return 3;
    // AgX and Neutral have no cheap closed form; ACES is the nearest in feel.
    default:
      return 4;
  }
}

/** Uniforms for the session grade. Only ever built inside a session. */
interface OverlayUniforms {
  /** `ShaderMaterial` wants an indexable uniform map; the named members are the real contract. */
  [name: string]: THREE.IUniform;
  uResolution: { value: THREE.Vector2 };
  uTime: { value: number };
  uSanity: { value: number };
  uDamage: { value: number };
  uDropout: { value: number };
  uDrain: { value: number };
  uComfort: { value: number };
  uGrain: { value: number };
  uGrade: { value: number };
}

class Chain implements PostFx {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;

  private readonly uniforms = {
    tDiffuse: { value: null as THREE.Texture | null },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uPixelRatio: { value: 1 },
    uTime: { value: 0 },
    uSanity: { value: 0 },
    uDamage: { value: 0 },
    uDropout: { value: 0 },
    uDrain: { value: 0 },
    uExposure: { value: 1 },
    uTone: { value: 0 },
  };

  private target: THREE.WebGLRenderTarget | null = null;
  private width = 1;
  private height = 1;
  private pixelRatio = 1;
  /** MSAA sample count for the offscreen pass; 0 when the renderer opted out. */
  private readonly samples: number;
  /** Half-float linear target, needed only when the renderer tone maps. */
  private hdr = false;
  private toneMapping: THREE.ToneMapping;

  private on = true;
  private time = 0;
  /** Whether `update` ran since the last frame; see the fallback in `render`. */
  private ticked = false;
  private sanity = 0;
  private sanityTarget = 0;
  private damage = 0;
  private dropout = 0;
  private drain = 0;
  private drainTarget = 0;

  /** Reused so the frame loop allocates nothing. */
  private readonly scratch = new THREE.Vector2();

  /* ---------------------------------------------------------------- *
   * Session grade. All null until an immersive session actually starts.
   * ---------------------------------------------------------------- */

  private readonly specId: string;
  /** Built with the overlay, so a desktop run does not allocate them at all. */
  private overlayUniforms: OverlayUniforms | null = null;
  private overlayMaterial: THREE.ShaderMaterial | null = null;
  private overlayMesh: THREE.Mesh | null = null;
  /** The camera the overlay is currently parented to, or null when detached. */
  private overlayHost: THREE.Object3D | null = null;

  constructor(renderer: THREE.WebGLRenderer, spec: LevelSpec) {
    this.renderer = renderer;
    this.specId = spec.id;
    this.pixelRatio = renderer.getPixelRatio() || 1;
    renderer.getDrawingBufferSize(this.scratch);
    this.width = Math.max(1, this.scratch.x | 0);
    this.height = Math.max(1, this.scratch.y | 0);

    const attrs = renderer.getContext().getContextAttributes();
    this.samples = attrs && attrs.antialias ? 4 : 0;

    this.toneMapping = renderer.toneMapping;
    this.hdr = this.toneMapping !== THREE.NoToneMapping;
    this.uniforms.uTone.value = toneMode(this.toneMapping);
    this.uniforms.uExposure.value = renderer.toneMappingExposure;

    // A single triangle rather than a quad: no diagonal seam, one fewer vertex,
    // and every pixel is shaded exactly once.
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3)
    );
    this.geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 2, 0, 0, 2]), 2));

    this.material = new THREE.ShaderMaterial({
      name: `postfx-${spec.id}`,
      uniforms: this.uniforms,
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      depthTest: false,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(this.geometry, this.material);
    mesh.frustumCulled = false;
    this.scene.add(mesh);
  }

  get enabled(): boolean {
    return this.on;
  }

  setEnabled(on: boolean): void {
    if (on === this.on) return;
    this.on = on;
    // Nothing to hold on to while bypassed; the target is a full framebuffer.
    if (!on) this.releaseTarget();
  }

  setSize(width: number, height: number, pixelRatio: number): void {
    this.pixelRatio = pixelRatio > 0 ? pixelRatio : 1;
    this.resize(Math.round(width * this.pixelRatio), Math.round(height * this.pixelRatio));
  }

  setSanity(v: number): void {
    this.sanityTarget = clamp01(v);
  }

  setDamage(v: number): void {
    // Take the larger of the incoming value and what is still decaying, so a
    // caller that sets a flash once and a caller that feeds a decaying value
    // every frame both behave.
    this.damage = Math.max(this.damage, clamp01(v));
  }

  setDrain(v: number): void {
    this.drainTarget = clamp01(v);
  }

  update(dt: number): void {
    this.ticked = true;
    this.time += dt;
    this.drain = damp(this.drain, this.drainTarget, 2.2, dt);
    this.sanity = damp(this.sanity, this.sanityTarget, 2.5, dt);
    this.damage = damp(this.damage, 0, 3.4, dt);
    if (this.damage < 0.002) this.damage = 0;

    // Single-frame dropouts, only once sanity is nearly gone. Cosmetic, so an
    // unseeded random is fine here.
    const deep = smoothstep(0.72, 1, this.sanity);
    this.dropout = deep > 0 && Math.random() < deep * dt * 0.6 ? 1 : 0;
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    // The whole XR path hangs off this one read, and `isPresenting` is false
    // for the entire life of a desktop session — every line below is the game
    // as it shipped. See the header for why none of it can run in a headset.
    if (this.renderer.xr.isPresenting) {
      this.renderStereo(scene, camera);
      return;
    }
    // A session that has just ended leaves the overlay parented to the camera.
    if (this.overlayHost) this.detachOverlay();

    if (!this.on) {
      this.renderer.setRenderTarget(null);
      this.renderer.render(scene, camera);
      return;
    }

    // Grain has to move even if whoever owns this forgot to call update().
    if (!this.ticked) this.time += 1 / 60;
    this.ticked = false;

    this.syncToneMapping();
    // The renderer is free to resize itself without telling us; the drawing
    // buffer is the authority for both size and pixel ratio.
    this.pixelRatio = this.renderer.getPixelRatio() || this.pixelRatio;
    this.renderer.getDrawingBufferSize(this.scratch);
    this.resize(this.scratch.x | 0, this.scratch.y | 0);
    const target = this.target ?? this.makeTarget();

    this.renderer.setRenderTarget(target);
    if (!this.renderer.autoClear) this.renderer.clear();
    this.renderer.render(scene, camera);

    this.uniforms.tDiffuse.value = target.texture;
    this.uniforms.uResolution.value.set(this.width, this.height);
    this.uniforms.uPixelRatio.value = this.pixelRatio;
    this.uniforms.uTime.value = this.time;
    this.uniforms.uSanity.value = this.sanity;
    this.uniforms.uDamage.value = this.damage;
    this.uniforms.uDropout.value = this.dropout;
    this.uniforms.uDrain.value = this.drain;
    this.uniforms.uExposure.value = this.renderer.toneMappingExposure;

    this.renderer.setRenderTarget(null);
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.releaseTarget();
    this.detachOverlay();
    this.overlayMaterial?.dispose();
    this.overlayMaterial = null;
    this.overlayMesh = null;
    this.overlayUniforms = null;
    this.geometry.dispose();
    this.material.dispose();
  }

  /* ---------------------------------------------------------------- *
   * Stereo
   * ---------------------------------------------------------------- */

  /**
   * One frame of an immersive session.
   *
   * Note what is *not* here: no `setRenderTarget`, in either direction. The
   * session's framebuffer was bound by `WebXRManager` before the frame
   * callback ran and it has to stay bound; touching it at all is how the
   * headset goes black.
   */
  private renderStereo(scene: THREE.Scene, camera: THREE.Camera): void {
    // Measured at the same point every frame, so the interval it sees is the
    // frame period. Driven from here rather than from the game loop because
    // the loop belongs to somebody else and a session should get its governor
    // whether or not anyone remembered to wire one up.
    xrQuality.tick(this.renderer);

    // Grain has to move even if whoever owns this forgot to call update().
    if (!this.ticked) this.time += 1 / 60;
    this.ticked = false;

    // A full-frame colour target is a few megabytes of a headset's memory
    // doing nothing at all: in here it is never rendered into and never
    // sampled. Handed back the moment a session starts.
    if (this.target) this.releaseTarget();

    if (this.on && xrPostGrade()) {
      this.attachOverlay(camera);
      this.syncOverlay();
    } else if (this.overlayHost) {
      this.detachOverlay();
    }

    // three sees an active session, swaps in the ArrayCamera and draws the
    // list once per eye into the framebuffer it already bound. The overlay is
    // a child of the camera, so it goes along with it — one extra blended
    // triangle per eye, and no second traversal.
    this.renderer.render(scene, camera);
  }

  /**
   * Park the grade under the camera.
   *
   * It has to be inside the scene graph rather than rendered as its own pass:
   * a second `renderer.render` in a session means a second XR camera update
   * and, where the driver has no `WEBGL_multisampled_render_to_texture`, a
   * second full-frame MSAA resolve — which is precisely the bandwidth this
   * whole design is trying not to spend. The camera is already in the scene
   * (the flashlight hangs off it the same way), and the vertex shader writes
   * clip space directly, so where the mesh sits is irrelevant: it fills
   * whichever eye's viewport is being drawn.
   */
  private attachOverlay(camera: THREE.Object3D): void {
    if (this.overlayHost === camera) return;
    if (this.overlayHost) this.detachOverlay();
    camera.add(this.overlayMesh ?? this.makeOverlay());
    this.overlayHost = camera;
  }

  private detachOverlay(): void {
    if (!this.overlayHost) return;
    if (this.overlayMesh) this.overlayHost.remove(this.overlayMesh);
    this.overlayHost = null;
  }

  private makeOverlay(): THREE.Mesh {
    this.overlayUniforms = {
      uResolution: { value: new THREE.Vector2(1, 1) },
      uTime: { value: 0 },
      uSanity: { value: 0 },
      uDamage: { value: 0 },
      uDropout: { value: 0 },
      uDrain: { value: 0 },
      uComfort: { value: 0 },
      uGrain: { value: 1 },
      uGrade: { value: 0 },
    };
    this.overlayMaterial = new THREE.ShaderMaterial({
      name: `postfx-xr-${this.specId}`,
      uniforms: this.overlayUniforms,
      vertexShader: VERTEX,
      fragmentShader: XR_FRAGMENT,
      transparent: true,
      // `frame * (1 - A) + acc`, which is what the shader accumulates.
      premultipliedAlpha: true,
      depthTest: false,
      depthWrite: false,
      // The shader writes finished sRGB. Neither of these injects anything
      // into a custom shader today; they are here so that stays true.
      toneMapped: false,
      fog: false,
    });

    // Shares the flat pass's single triangle. Disposed with it, once.
    const mesh = new THREE.Mesh(this.geometry, this.overlayMaterial);
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    // Nearly last. `render/xrvignette.ts` sits at 10000 and has to stay on the
    // outside of everything: its tunnel is the edge of vision, and grain
    // sprinkled over the black would give the blackout a texture to be a
    // surface rather than an absence.
    mesh.renderOrder = 9990;
    this.overlayMesh = mesh;
    return mesh;
  }

  private syncOverlay(): void {
    const u = this.overlayUniforms;
    if (!u) return;
    const xr = TUNING.render.xr;

    // One eye's viewport, not the drawing buffer: the aspect and the grain
    // scale both have to be per eye. Empty on the first frame of a session,
    // before three has seen a pose.
    const eye = this.renderer.xr.getCamera().cameras[0];
    const vp = eye ? eye.viewport : null;
    if (vp && vp.z > 0 && vp.w > 0) {
      u.uResolution.value.set(vp.z, vp.w);
    } else {
      this.renderer.getDrawingBufferSize(this.scratch);
      u.uResolution.value.set(Math.max(1, this.scratch.x), Math.max(1, this.scratch.y));
    }

    u.uTime.value = this.time;
    u.uSanity.value = this.sanity;
    u.uDamage.value = this.damage;
    u.uDrain.value = this.drain;
    u.uDropout.value = this.dropout * xr.dropoutScale;
    u.uComfort.value = xrComfort();
    u.uGrain.value = xr.grainScale;
    u.uGrade.value = xr.sanityGrade;
  }

  /* ---------------------------------------------------------------- *
   * Target management
   * ---------------------------------------------------------------- */

  private resize(width: number, height: number): void {
    const w = Math.max(1, width);
    const h = Math.max(1, height);
    if (w === this.width && h === this.height && this.target) return;
    this.width = w;
    this.height = h;
    if (this.target) this.target.setSize(w, h);
  }

  private makeTarget(): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(this.width, this.height, {
      format: THREE.RGBAFormat,
      type: this.hdr ? THREE.HalfFloatType : THREE.UnsignedByteType,
      // An 8-bit target holds sRGB so the dark end does not band; an HDR target
      // stays linear because the tone curve has not been applied yet.
      colorSpace: this.hdr ? THREE.NoColorSpace : THREE.SRGBColorSpace,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: true,
      stencilBuffer: false,
      generateMipmaps: false,
      samples: this.samples,
    });
    target.texture.name = 'postfx-scene';
    this.target = target;
    return target;
  }

  private releaseTarget(): void {
    if (!this.target) return;
    this.target.dispose();
    this.target = null;
    this.uniforms.tDiffuse.value = null;
  }

  /** Follow the renderer if it changes its tone mapping after construction. */
  private syncToneMapping(): void {
    const mapping = this.renderer.toneMapping;
    if (mapping === this.toneMapping) return;
    this.toneMapping = mapping;
    this.uniforms.uTone.value = toneMode(mapping);
    const hdr = mapping !== THREE.NoToneMapping;
    if (hdr === this.hdr) return;
    this.hdr = hdr;
    this.releaseTarget();
  }
}

/**
 * `PostFx` plus the light-drain input.
 *
 * It is not on the shared `PostFx` contract in `render/api.ts` because nothing
 * outside the renderer sets it, and the renderer builds this chain directly
 * rather than through the interface.
 */
export interface DrainablePostFx extends PostFx {
  /** 0..1 light drain where the player is standing. Decays on its own. */
  setDrain(v: number): void;
}

export function makePostFx(renderer: THREE.WebGLRenderer, spec: LevelSpec): DrainablePostFx {
  return new Chain(renderer, spec);
}
